import { type RovoChatOpenMode } from '@atlaskit/rovo-triggers/params-types';

/**
 * Transforms Confluence URLs to include page view attribution for ROVOCHAT.
 * Used for page view attribution in Confluence. More info: https://hello.atlassian.net/wiki/x/ViVNCQE
 */
export const transformIfConfluenceLink = (
	url: string,
	shouldAddAttribution: boolean = false,
): string => {
	if (!url.includes('/wiki/spaces/') || !shouldAddAttribution) {
		return url;
	}

	try {
		const urlObj = new URL(url);
		urlObj.searchParams.set('atl_f', 'ROVOCHAT');
		return urlObj.toString();
	} catch {
		// Fallback to original URL if URL parsing fails
		return url;
	}
};

export const transformRovoChatDeepLink = (
	urlString: string,
	conversationId?: string,
	openChatMode?: RovoChatOpenMode,
): URL => {
	const url = new URL(urlString);

	url.searchParams.set('rovoChatTriggerOpen', 'true');
	if (conversationId) {
		url.searchParams.set('rovoChatConversationId', conversationId);
		url.searchParams.set('rovoChatPathway', 'chat');
	}
	if (openChatMode) {
		url.searchParams.set('rovoChatOpenChatMode', openChatMode);
	}

	return url;
};

/**
 * Checks if a text string looks like a URL (starts with http:// or https://)
 */
export const isUrlLike = (text: string): boolean => {
	return /^https?:\/\//.test(text.trim());
};

/**
 * Checks if a URL is a Slack URL
 */
export const isSlackUrl = (urlString: string): boolean => {
	const url = new URL(urlString);
	return url.hostname.includes('slack.com');
};

/**
 * Determines if the provided title resolves to a readable title.
 * Returns true if we should extract a title from the URL instead (no title, title is URL-like, or PDF).
 */
export const resolvesToReadableTitle = (url: string, title?: string): boolean => {
	if (!title) {
		return false;
	}

	if (isUrlLike(title)) {
		return false;
	}

	const urlObj = new URL(url);
	return !urlObj.searchParams.has('preview');
};

/**
 * Determines if SmartCard resolution should be skipped for a URL.
 * Returns true for URLs that SmartCard can't resolve like PDFs.
 */
export const shouldSkipSmartCardResolution = (url: string): boolean => {
	const urlObj = new URL(url);
	return urlObj.searchParams.has('preview');
};

/**
 * Extracts a readable title from a URL for file attachments (PDFs, markdown, etc.).
 */
export const extractReadableTitleFromUrl = (url: string): string => {
	const urlObj = new URL(url);

	const previewParam = urlObj.searchParams.get('preview');
	if (!previewParam) {
		return '';
	}

	// Extract filename from preview param
	const pathParts = previewParam.split('/');
	const filename = pathParts[pathParts.length - 1];
	if (!filename) {
		return '';
	}

	try {
		const decoded = decodeURIComponent(filename).replace(/\+/g, ' ').trim();
		return decoded && decoded.length > 0 ? decoded : '';
	} catch {
		return '';
	}
};

/**
 * Strips query parameters from Slack URLs to help SmartCard resolve them better.
 * Returns the URL without query parameters for Slack URLs, original URL otherwise.
 */
export const stripSlackQueryParams = (url: string): string => {
	if (isSlackUrl(url)) {
		const urlObj = new URL(url);
		return `${urlObj.protocol}//${urlObj.hostname}${urlObj.pathname}`;
	}
	return url;
};
